import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

const getFlagEmoji = (countryCode) => {
  if (!countryCode) return "🌍";
  return String(countryCode)
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt()));
};

const fmtDate = (d) => {
  if (!d) return "-";
  try {
    return format(new Date(d), "dd/MM/yyyy HH:mm");
  } catch {
    return "-";
  }
};

const money = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return "-";
  return n % 1 === 0 ? String(n) : String(Number(n.toFixed(2)));
};

const senderFullName = (u) =>
  [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() ||
  u?.email ||
  "-";

export default function TransactionDetailsView({ apiUrl, transactionId }) {
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = useMemo(
    () => String(apiUrl || "").replace(/\/+$/, ""),
    [apiUrl],
  );

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const res = await fetchWithAuth(
          `${API_BASE}/api/admin/transactions/${transactionId}`,
        );
        if (!res.ok) throw new Error("Transaction non trouvée");
        const data = await res.json();
        setTransaction(data.transaction || data);
      } catch (err) {
        console.error(err);
        alert(err.message);
        navigate("/admin/transactions");
      } finally {
        setLoading(false);
      }
    };

    fetchTransaction();
  }, [API_BASE, transactionId, navigate]);

  const handleDownload = () => {
    if (!transaction) return;

    const tx = transaction;
    const doc = new jsPDF({ unit: "pt" });
    const margin = 40;

    const img1 = new Image();
    const img2 = new Image();

    img1.crossOrigin = "Anonymous";
    img2.crossOrigin = "Anonymous";

    img1.src = "/ngc-logo.jpeg";
    img2.src = "/direct-money.jpeg";

    Promise.all([
      new Promise((resolve) => {
        img1.onload = resolve;
        img1.onerror = resolve;
      }),
      new Promise((resolve) => {
        img2.onload = resolve;
        img2.onerror = resolve;
      }),
    ]).then(() => {
      const user = tx.user || {};
      const profile = user.profile || {};

      const senderName = senderFullName(user);
      const senderEmail = user.email || "-";
      const senderPhone = user.phoneNumber || profile.phone || "-";
      const senderCountry = profile.country || profile.countryCode || "-";
      const senderAddress = profile.address || "-";

      const pageWidth = doc.internal.pageSize.getWidth();

      try {
        doc.addImage(img1, "JPEG", margin, 15, 90, 45);
      } catch {}

      try {
        doc.addImage(img2, "JPEG", pageWidth - 130, 15, 90, 45);
      } catch {}

      doc.setDrawColor(21, 37, 86);
      doc.setLineWidth(1.5);
      doc.line(margin, 70, pageWidth - margin, 70);

      doc.setFontSize(22);
      doc.setFont(undefined, "bold");
      doc.setTextColor(21, 37, 86);

      doc.text("RELEVÉ DE TRANSACTION", pageWidth / 2, 40, { align: "center" });

      doc.setFontSize(11);
      doc.setFont(undefined, "normal");
      doc.setTextColor(80);

      doc.text(`Référence : ${tx.paymentRef || tx.id}`, pageWidth / 2, 55, {
        align: "center",
      });

      doc.text(`Date : ${fmtDate(tx.createdAt)}`, pageWidth / 2, 65, {
        align: "center",
      });

      let currentY = 90;

      autoTable(doc, {
        startY: currentY,
        head: [["Champ", "Valeur"]],
        body: [
          ["Référence", tx.paymentRef || "-"],
          ["ID", String(tx.id)],
          ["Statut", (tx.status || "-").toUpperCase()],
          ["Type", tx.type || "-"],
          ["Montant envoyé", `${money(tx.amount)} ${tx.currency || ""}`],
          ["Frais", `${money(tx.fee)} ${tx.currency || ""}`],
          [
            "Taux de change",
            tx.exchange_rate != null ? String(tx.exchange_rate) : "-",
          ],
          [
            "Montant converti",
            tx.convertedAmount && tx.recipientCurrency
              ? `${money(tx.convertedAmount)} ${tx.recipientCurrency}`
              : "-",
          ],
          ["Méthode", tx.method || "-"],
          ["Pays destination", tx.destinationCountry || "-"],
          ["Commentaire admin", tx.adminComment || "-"],
          ["Note utilisateur", tx.note || "-"],
          //["Finalisée", tx.isFinalized ? "Oui" : "Non"],
          ["Masquée par user", tx.hiddenByUser ? "Oui" : "Non"],
          ["Masquée le", fmtDate(tx.hiddenAt)],
          ["Annulée le", fmtDate(tx.cancelledAt)],
          ["Annulée par", tx.cancelledBy || "-"],
        ],
        theme: "grid",
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 5 },
      });

      currentY = doc.lastAutoTable.finalY + 15;

      autoTable(doc, {
        startY: currentY,
        head: [["Émetteur (Sender)", "Valeur"]],
        body: [
          ["Nom", senderName],
          ["Email", senderEmail],
          ["Téléphone", senderPhone],
          ["Pays", senderCountry],
          ["Adresse", senderAddress],
        ],
        theme: "grid",
        headStyles: { fillColor: [39, 174, 96], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 5 },
      });

      currentY = doc.lastAutoTable.finalY + 15;

      autoTable(doc, {
        startY: currentY,
        head: [["Bénéficiaire (Receiver)", "Valeur"]],
        body: [
          ["Nom", tx.recipientName || "-"],
          ["Prénom", tx.recipientFirstName || "-"],
          ["Nom (2)", tx.recipientLastName || "-"],
          ["Email", tx.recipientEmail || "-"],
          ["Téléphone", tx.recipientPhone || "-"],
          ["Devise réception", tx.recipientCurrency || "-"],
        ],
        theme: "grid",
        headStyles: { fillColor: [231, 76, 60], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 5 },
      });

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(9);
        doc.setTextColor(100);
        doc.text(
          "Ngc Financial services | info@ngcfinancialservices.com",
          margin,
          pageHeight - 30,
        );
        doc.text(
          `Relevé téléchargé le : ${fmtDate(new Date())}`,
          margin,
          pageHeight - 15,
        );
      }

      doc.setFontSize(8);
      doc.setTextColor(120);

      doc.save(`transaction_${tx.paymentRef || tx.id}.pdf`);
    });
  };

  if (loading) return <div className="p-6">Chargement...</div>;
  if (!transaction) return null;

  const tx = transaction;
  const user = tx.user || {};
  const profile = user.profile || {};

  const senderName = senderFullName(user);
  const senderEmail = user.email || "-";
  const senderPhone = user.phoneNumber || profile.phone || "-";
  const senderCountry = profile.country || profile.countryCode || "-";
  const senderAddress = profile.address || "-";

  return (
    <div className="p-3 sm:p-4 md:p-6 min-h-[calc(100vh-6rem)] bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Relevé transaction #{tx.paymentRef || tx.id}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-300 mt-1">
              Créée le : {fmtDate(tx.createdAt)}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate("/admin/transactions")}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg text-sm dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
            >
              ← Retour
            </button>

            <button
              onClick={handleDownload}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
            >
              ⬇ Télécharger le relevé
            </button>
          </div>
        </div>

        {/* Main info */}
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Informations transaction
          </h3>

          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-800 dark:text-gray-100">
            <p>
              <span className="font-semibold">Référence :</span>{" "}
              {tx.paymentRef || "-"}
            </p>
            <p>
              <span className="font-semibold">ID :</span> {tx.id}
            </p>

            <p>
              <span className="font-semibold">Statut :</span>{" "}
              {(tx.status || "-").toUpperCase()}
            </p>
            <p>
              <span className="font-semibold">Type :</span> {tx.type || "-"}
            </p>

            <p>
              <span className="font-semibold">Montant envoyé :</span>{" "}
              {money(tx.amount)} {tx.currency || ""}
            </p>
            <p>
              <span className="font-semibold">Frais :</span> {money(tx.fee)}{" "}
              {tx.currency || ""}
            </p>

            <p>
              <span className="font-semibold">Taux de change :</span>{" "}
              {tx.exchange_rate != null ? String(tx.exchange_rate) : "-"}
            </p>

            <p>
              <span className="font-semibold">Montant converti :</span>{" "}
              {tx.convertedAmount && tx.recipientCurrency
                ? `${money(tx.convertedAmount)} ${tx.recipientCurrency}`
                : "-"}
            </p>

            <p>
              <span className="font-semibold">Méthode :</span>{" "}
              {tx.method || "-"}
            </p>
            <p>
              <span className="font-semibold">Pays destination :</span>{" "}
              {getFlagEmoji(tx.destinationCountry)}{" "}
              {tx.destinationCountry || "-"}
            </p>

            <p>
              <span className="font-semibold">Finalisée :</span>{" "}
              {tx.isFinalized ? "Oui" : "Non"}
            </p>
            <p>
              <span className="font-semibold">Masquée par user :</span>{" "}
              {tx.hiddenByUser ? "Oui" : "Non"}
            </p>

            <p>
              <span className="font-semibold">Masquée le :</span>{" "}
              {fmtDate(tx.hiddenAt)}
            </p>
            <p>
              <span className="font-semibold">Annulée le :</span>{" "}
              {fmtDate(tx.cancelledAt)}
            </p>

            <p>
              <span className="font-semibold">Annulée par :</span>{" "}
              {tx.cancelledBy || "-"}
            </p>
            <p>
              <span className="font-semibold">Commentaire admin :</span>{" "}
              {tx.adminComment || "-"}
            </p>

            <p className="sm:col-span-2">
              <span className="font-semibold">Note utilisateur :</span>{" "}
              {tx.note || "-"}
            </p>
          </div>
        </section>

        {/* Sender */}
        <section className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Informations émetteur (Sender)
          </h3>

          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-800 dark:text-gray-100">
            <p>
              <span className="font-semibold">Nom :</span> {senderName}
            </p>
            <p>
              <span className="font-semibold">Email :</span> {senderEmail}
            </p>
            <p>
              <span className="font-semibold">Téléphone :</span> {senderPhone}
            </p>
            <p>
              <span className="font-semibold">Pays :</span> {senderCountry}
            </p>
            <p className="sm:col-span-2">
              <span className="font-semibold">Adresse :</span> {senderAddress}
            </p>
          </div>
        </section>

        {/* Receiver */}
        <section>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Informations bénéficiaire (Receiver)
          </h3>

          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-800 dark:text-gray-100">
            <p>
              <span className="font-semibold">Nom :</span>{" "}
              {tx.recipientName || "-"}
            </p>
            <p>
              <span className="font-semibold">Téléphone :</span>{" "}
              {tx.recipientPhone || "-"}
            </p>
            <p>
              <span className="font-semibold">Email :</span>{" "}
              {tx.recipientEmail || "-"}
            </p>
            <p>
              <span className="font-semibold">Devise réception :</span>{" "}
              {tx.recipientCurrency || "-"}
            </p>
            <p>
              <span className="font-semibold">Prénom :</span>{" "}
              {tx.recipientFirstName || "-"}
            </p>
            <p>
              <span className="font-semibold">Nom (2) :</span>{" "}
              {tx.recipientLastName || "-"}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
